import React, { useState, useEffect, useRef } from 'react';
import { heroData } from '../data/mock';
import { ArrowRight, GraduationCap, Sparkles, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

// University data with logos
const universities = [
  { 
    name: 'Harvard', 
    logo: '/assets/logos/harvard.png',
    delay: 0.2, 
    position: { top: '15%', left: '6%' } 
  },
  { 
    name: 'Yale', 
    logo: '/assets/logos/yale.png',
    delay: 0.5, 
    position: { top: '22%', right: '8%' } 
  },
  { 
    name: 'Princeton', 
    logo: '/assets/logos/princeton.png',
    delay: 0.8, 
    position: { bottom: '42%', left: '4%' } 
  },
  { 
    name: 'MIT', 
    logo: '/assets/logos/mit.png',
    delay: 1.1, 
    position: { top: '50%', right: '4%' } 
  },
  { 
    name: 'Stanford', 
    logo: '/assets/logos/stanford.png',
    delay: 1.4, 
    position: { bottom: '25%', right: '10%' } 
  },
  { 
    name: 'Columbia', 
    logo: '/assets/logos/columbia.png',
    delay: 1.7, 
    position: { bottom: '15%', left: '10%' } 
  },
  { 
    name: 'Vanderbilt', 
    logo: '/assets/logos/vanderbilt.png',
    delay: 2.0, 
    position: { top: '65%', left: '2%' } 
  },
];

// Floating university badge component with real logos
const UniversityBadge = ({ name, logo, delay, position, isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`absolute hidden lg:flex items-center gap-3 px-4 py-3 rounded-2xl backdrop-blur-md shadow-xl transition-all duration-700 cursor-pointer hover:scale-110 group ${
        isDarkMode 
          ? 'bg-[#1a2625]/80 border border-[#284139] hover:bg-[#284139]/90' 
          : 'bg-[#284139]/80 border border-[#284139] hover:bg-[#284139]/90'
      } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{
        ...position,
        animation: isVisible ? `float-gentle ${3 + delay}s ease-in-out infinite` : 'none',
        animationDelay: `${delay}s`,
      }}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white p-1">
        <img 
          src={logo} 
          alt={`${name} logo`}
          className="w-full h-full object-contain"
        />
      </div>
      <span className={`text-sm font-medium ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>{name}</span>
    </div>
  );
};

// Interactive cursor follower
const CursorGlow = ({ isDarkMode }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);
    };

    const handleMouseLeave = () => setIsVisible(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div
      className="pointer-events-none fixed w-[400px] h-[400px] rounded-full transition-opacity duration-300"
      style={{
        background: isDarkMode 
          ? 'radial-gradient(circle, rgba(229, 217, 182, 0.08) 0%, transparent 70%)'
          : 'radial-gradient(circle, rgba(40, 65, 57, 0.08) 0%, transparent 70%)',
        left: position.x - 200,
        top: position.y - 200,
        opacity: isVisible ? 1 : 0,
        zIndex: 0,
      }}
    />
  );
};

const Hero = ({ isDarkMode }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      <CursorGlow isDarkMode={isDarkMode} />
      
      {/* Background Elements */}
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-[#111A19]' : 'bg-[#E5D9B6]'}`}>
        {/* Animated gradient mesh that follows mouse */}
        <div 
          className="absolute inset-0 transition-all duration-1000 ease-out"
          style={{
            background: isDarkMode 
              ? `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, rgba(229, 217, 182, 0.1) 0%, transparent 50%)`
              : `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, rgba(40, 65, 57, 0.1) 0%, transparent 50%)`,
          }}
        />
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-[#284139]/20 via-transparent to-[#111A19]' : 'bg-gradient-to-br from-[#284139]/10 via-transparent to-[#E5D9B6]'}`} />
        
        {/* Animated orbs */}
        <div className={`absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[100px] animate-glow-pulse ${isDarkMode ? 'bg-[#E5D9B6]/5' : 'bg-[#284139]/10'}`} />
        <div className={`absolute bottom-1/4 left-1/4 w-[400px] h-[400px] rounded-full blur-[80px] animate-glow-pulse ${isDarkMode ? 'bg-[#284139]/20' : 'bg-[#284139]/10'}`} style={{ animationDelay: '1s' }} />
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 rounded-full ${isDarkMode ? 'bg-[#E5D9B6]/30' : 'bg-[#284139]/30'}`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float-particle ${5 + Math.random() * 10}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(${isDarkMode ? '#809076' : '#284139'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#809076' : '#284139'} 1px, transparent 1px)`,
            backgroundSize: '80px 80px'
          }}
        />
      </div>

      {/* Floating University Badges */}
      {universities.map((uni, index) => (
        <UniversityBadge key={uni.name} {...uni} isDarkMode={isDarkMode} />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge with shimmer */}
          <div className={`inline-flex items-center space-x-2 px-5 py-2.5 rounded-full mb-10 animate-fade-in backdrop-blur-sm group transition-colors cursor-default ${
            isDarkMode 
              ? 'bg-[#284139]/40 border border-[#284139] hover:bg-[#284139]/60' 
              : 'bg-[#284139]/20 border border-[#284139] hover:bg-[#284139]/30'
          }`}>
            <GraduationCap size={18} className={isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'} />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-[#809076]' : 'text-[#284139]'}`}>
              College Counseling & Leadership Development
            </span>
            <Sparkles size={14} className={`animate-pulse ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'}`} />
          </div>

          {/* Title with gradient */}
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold mb-6 animate-fade-in-up leading-[1.1]">
            <span className={`block ${isDarkMode ? 'text-white' : 'text-[#284139]'}`}>PATHWAYS</span>
            <span className={`block ${isDarkMode ? 'text-white' : 'text-[#284139]'}`}>
              AFRICA
            </span>
          </h1>

          {/* New Subtitle */}
          <p className="text-xl sm:text-2xl lg:text-3xl font-medium mb-6 animate-fade-in-up stagger-1 tracking-wide">
            <span className={isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'}> FROM DREAM SCHOOLS TO DECISION LETTERS</span>
          </p>

          {/* Description */}
          <p className={`text-lg max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in-up stagger-2 ${isDarkMode ? 'text-[#809076]/80' : 'text-[#284139]/70'}`}>
            {heroData.description}
          </p>

          {/* CTA Buttons with enhanced interactivity */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 animate-fade-in-up stagger-3">
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSdElR9Gt7aHl0IKSVAiMXkEdQkZ4Aijo2YrrTvZzm6QnSg8Lg/viewform"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className={`group relative px-10 py-7 font-semibold text-lg rounded-xl btn-transition flex items-center gap-3 shadow-xl hover:scale-105 overflow-hidden ${
                  isDarkMode 
                    ? 'bg-[#E5D9B6] hover:bg-[#d4c9a5] text-[#111A19] shadow-[#E5D9B6]/30 hover:shadow-[#E5D9B6]/50'
                    : 'bg-[#284139] hover:bg-[#1a2625] text-[#E5D9B6] shadow-[#284139]/30 hover:shadow-[#284139]/50'
                }`}
              >
                <span className="relative z-10">Apply Now</span>
                <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </Button>
            </a>
            <Button
              variant="outline"
              className={`px-10 py-7 border-2 font-semibold text-lg rounded-xl btn-transition backdrop-blur-sm hover:scale-105 ${
                isDarkMode 
                  ? 'border-[#284139] bg-[#284139]/20 hover:bg-[#284139]/40 hover:border-[#E5D9B6]/50 text-[#f5f5f5]'
                  : 'border-[#284139] bg-[#284139]/10 hover:bg-[#284139]/20 hover:border-[#284139] text-[#284139]'
              }`}
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group"
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
      >
        <span className={`text-sm font-medium tracking-wide transition-colors ${isDarkMode ? 'text-[#809076] group-hover:text-[#E5D9B6]' : 'text-[#284139]/70 group-hover:text-[#284139]'}`}>Scroll Down</span>
        <ChevronDown className={`w-6 h-6 animate-bounce ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'}`} />
      </div>

      {/* Decorative corner elements */}
      <div className={`absolute top-20 left-0 w-64 h-64 border-l-2 border-t-2 rounded-tl-3xl ${isDarkMode ? 'border-[#284139]/30' : 'border-[#284139]/20'}`} />
      <div className={`absolute bottom-0 right-0 w-64 h-64 border-r-2 border-b-2 rounded-br-3xl ${isDarkMode ? 'border-[#284139]/30' : 'border-[#284139]/20'}`} />
    </section>
  );
};

export default Hero;
